/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import clsx from 'clsx';
import React, { useMemo } from 'react';
import { hasPermission } from '../../Utilities/HasPermission/HasPermission';
import { isFeatureEnabled } from '../../Utilities/IsFeatureEnabled/IsFeatureEnabled';
import { DashboardCardProps } from '../DashboardCard/DashboardCard.props';
import { DashboardCardGrid } from '../DashboardCardGrid/DashboardCardGrid';
import { DxpScreenBanner } from '../DxpScreenBanner/DxpScreenBanner';
import { DxpScreenBannerProps } from '../DxpScreenBanner/DxpScreenBanner.props';
import { CapabilityDashboardProps } from './CapabilityDashboard.props';

/**
 * Capability dashboard.
 */
export const CapabilityDashboard = (props: CapabilityDashboardProps): JSX.Element => {
  const { classes, dashboardCards, description, icon, title } = props;

  const dashboardClass = clsx(`capability-dashboard`, classes);

  const filteredCards = useMemo(() => {
    return dashboardCards.filter((card: DashboardCardProps) => {
      const { featureFlag, permission } = card;

      // If a certain permission level is required,
      // but not met, prevent access
      if (permission && !hasPermission(permission)) {
        return false;
      }

      // If a feature flag controls access to the capability,
      // but it isn't enabled, prevent access
      if (featureFlag && !isFeatureEnabled(featureFlag)) {
        return false;
      }

      return true;
    });
  }, [dashboardCards]);

  const bannerProps: DxpScreenBannerProps = {
    classes: `capability-dashboard-banner`,
    description,
    icon,
    title,
  };

  return (
    <div className={dashboardClass}>
      <DxpScreenBanner {...bannerProps} />
      <DashboardCardGrid dashboardCards={filteredCards} />
    </div>
  );
};
